/* Padel USA — PDP "How it plays" + "Compare with similar rackets".
 * Reuses the comparator's validation, radar and search (window.PadelRacketCompare from
 * assets/racket-comparator.js). Loads lazily from the racket-play-profile-data_custom section, which
 * returns ONE racket's context (2026-10-08, owner: no ratings data public): the viewed racket, up to 3
 * nearest rackets and a search list. Since 2026-10-31 the theme holds no scores: values and the catalog
 * median come from ratings.padelusa.com via window.PadelRatings (assets/racket-ratings-client.js). A
 * racket picked from search is fetched from its own page (racket-play-profile-one_custom). Read-only: no cart, catalog or
 * analytics writes. Text is set with textContent; the only parsed markup is the comparator's own
 * escaped radar SVG.
 * Dossier: custom-code-padelusa/features/racket-play-profile.md */
(() => {
  'use strict';
  if (customElements.get('racket-play-profile')) return;

  const DATA_SECTION = 'racket-play-profile-data_custom';
  const ONE_SECTION = 'racket-play-profile-one_custom';
  const MAX_COMPARE = 2;
  const SUGGESTIONS = 3;
  const NOUN = { power: 'power', control: 'control', comfort: 'comfort', spin: 'spin', forgiveness: 'forgiveness', handling: 'maneuverability' };
  // Same number format as the comparator page (plain: 8, 7.6) so bars and the score panel agree.
  const fmt = (n) => String(Number(n.toFixed(1)));
  const listJoin = (items) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`);

  /** Tiny element builder: attributes via setAttribute, text via textContent (never parsed). */
  function h(tag, attrs = {}, ...children) {
    const el = document.createElement(tag);
    for (const [name, value] of Object.entries(attrs)) {
      if (value === false || value === null || value === undefined) continue;
      if (name === 'text') el.textContent = value;
      else if (name === 'style') Object.entries(value).forEach(([prop, v]) => el.style.setProperty(prop, v));
      else el.setAttribute(name, value === true ? '' : String(value));
    }
    el.append(...children.flat().filter((child) => child !== null && child !== undefined && child !== false));
    return el;
  }

  class RacketPlayProfile extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      // The skeleton is server-rendered (no layout shift); only a failed load removes it.
      const load = () => this.load().catch(() => this.closest('.rpp')?.remove());
      if (!('IntersectionObserver' in window)) return void load();
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        load();
      }, { rootMargin: '1500px 0px' });
      io.observe(this);
    }

    async load() {
      const api = await this.waitForApi();
      const url = new URL(this.dataset.productUrl || location.pathname, location.origin);
      url.searchParams.set('section_id', DATA_SECTION);
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error('data');
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const data = JSON.parse(doc.querySelector('[data-rpp-catalog]').textContent);
      this.api = api;
      this.currency = data.currency || 'USD';
      // Scores come from the ratings API by product id (one request: this racket + up to 3 nearest).
      const near = (data.near || []).slice(0, SUGGESTIONS);
      const rated = await this.waitForRatings().then((ratings) => ratings.scores([data.current, ...near].filter(Boolean).map((p) => p.id)));
      const withValues = (p) => (p && rated.values[p.id] ? { ...p, verifiedValues: rated.values[p.id] } : null);
      this.ratings = {};
      this.cache = new Map();
      this.current = this.remember(withValues(data.current));
      if (!this.current || this.current.handle !== this.dataset.handle) throw new Error('profile');
      this.average = rated.median;
      if (!this.average || !api.AXES.every((key) => typeof this.average[key] === 'number')) throw new Error('median');
      // Search list: public card facts only — values load when a racket is picked.
      this.products = data.index || [];
      this.selected = [];
      this.extra = [];
      this.suggested = near.map((p) => this.remember(withValues(p))).filter(Boolean);
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.currency });

      this.querySelector('[data-rpp-lede]').textContent = this.lede();
      this.bindSearch();
      // Exact-score panel: the comparator page's own ScoreTooltip (hover, focus, arrows, tap to pin, Close).
      this.scores = new api.ScoreTooltip({
        chart: this.querySelector('[data-rpp-chart]'),
        tooltip: this.querySelector('[data-rpp-tooltip]'),
        mobile: window.matchMedia('(max-width: 749px)'),
        products: () => this.compared(),
        ratings: () => this.ratings,
      });
      this.scores.bind(new AbortController().signal);
      this.render();
      this.setAttribute('data-ready', '');
    }

    waitForRatings() {
      return new Promise((resolve, reject) => {
        let tries = 0;
        const check = () => {
          if (window.PadelRatings) return resolve(window.PadelRatings);
          if (++tries > 100) return reject(new Error('ratings'));
          setTimeout(check, 50);
        };
        check();
      });
    }

    waitForApi() {
      return new Promise((resolve, reject) => {
        let tries = 0;
        const check = () => {
          if (window.PadelRacketCompare) return resolve(window.PadelRacketCompare);
          if (++tries > 100) return reject(new Error('api'));
          setTimeout(check, 50);
        };
        check();
      });
    }

    /** Keep a racket with complete server-verified values; anything incomplete is dropped (no estimates). */
    remember(p) {
      if (!p) return null;
      const values = this.api.profile(p, false, {}).values;
      if (!this.api.AXES.every((key) => typeof values[key] === 'number')) return null;
      const racket = { ...p, values };
      this.cache.set(racket.handle, racket);
      return racket;
    }

    /** A racket picked from search: fetch only that racket from its own product page. */
    async fetchOne(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);
      const entry = this.products.find((p) => p.handle === handle);
      if (!entry) return null;
      const url = new URL(entry.url, location.origin);
      url.searchParams.set('section_id', ONE_SECTION);
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) return null;
      const node = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('[data-rpp-one]');
      if (!node) return null;
      const reply = JSON.parse(node.textContent);
      const rated = await (await this.waitForRatings()).scores([reply.id]);
      return rated.values[reply.id] ? this.remember({ ...reply, verifiedValues: rated.values[reply.id] }) : null;
    }

    lede() {
      const gaps = this.api.AXES.map((key) => ({ key, gap: this.current.values[key] - this.average[key] }));
      const strengths = gaps.filter((g) => g.gap >= 0.3).sort((a, b) => b.gap - a.gap).slice(0, 2);
      const weakest = gaps.filter((g) => g.gap <= -0.5).sort((a, b) => a.gap - b.gap)[0];
      if (!strengths.length && !weakest) {
        return 'A balanced racket: every attribute sits close to the average of the rackets we rate.';
      }
      const lead = strengths.length
        ? `Stands out for ${listJoin(strengths.map((g) => NOUN[g.key]))}.`
        : 'Close to average on most attributes.';
      return weakest ? `${lead} Offers less ${NOUN[weakest.key]} than most rackets we rate.` : lead;
    }

    compared() {
      return [this.current, ...this.selected.map((handle) => this.byHandle(handle)).filter(Boolean)];
    }

    byHandle(handle) {
      return this.cache.get(handle);
    }

    render() {
      const rackets = this.compared();
      this.scores?.hide();
      this.renderChart(rackets);
      this.renderBars(rackets);
      this.renderCards();
      this.renderFullLink();
    }

    renderChart(rackets) {
      const host = this.querySelector('[data-rpp-plot]');
      // The comparator's svgChart escapes every title it prints; parse it as SVG, not HTML.
      const parsed = new DOMParser().parseFromString(
        this.api.svgChart(rackets, false, this.ratings).replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" '),
        'image/svg+xml'
      );
      const svg = parsed.documentElement;
      if (!svg || svg.nodeName !== 'svg') return;
      const geometry = (i, score) => {
        const angle = i * Math.PI / 3 - Math.PI / 2;
        return `${(230 + Math.cos(angle) * 151 * score / 10).toFixed(2)},${(230 + Math.sin(angle) * 151 * score / 10).toFixed(2)}`;
      };
      const average = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      average.setAttribute('class', 'rpp__avg-shape');
      average.setAttribute('points', this.api.AXES.map((key, i) => geometry(i, this.average[key])).join(' '));
      svg.querySelector('.rc__series')?.before(average);
      host.replaceChildren(document.importNode(svg, true));
    }

    renderBars(rackets) {
      // Fill the server-rendered rows in place: one fill for this racket, dots for compared rackets.
      // Row heights never change, so comparing causes no layout shift.
      this.querySelectorAll('[data-rpp-axis]').forEach((row) => {
        const key = row.dataset.rppAxis;
        const label = row.querySelector('.rpp__bar-label')?.textContent || key;
        row.querySelector('[data-rpp-fill]').style.setProperty('--rpp-value', `${this.current.values[key] * 10}%`);
        row.querySelector('[data-rpp-avg]').style.setProperty('--rpp-avg', `${this.average[key] * 10}%`);
        [0, 1, 2].forEach((i) => {
          const p = rackets[i];
          const value = row.querySelector(`[data-rpp-value="${i}"]`);
          value.textContent = p ? fmt(p.values[key]) : '';
          const dot = row.querySelector(`[data-rpp-dot="${i}"]`);
          if (!dot) return;
          dot.toggleAttribute('data-on', Boolean(p));
          if (p) dot.style.setProperty('--rpp-value', `${p.values[key] * 10}%`);
        });
        row.querySelector('[data-rpp-sr]').textContent = `${label}: this racket ${fmt(this.current.values[key])} out of 10, average ${fmt(this.average[key])}` +
          rackets.slice(1).map((p) => `; ${p.title} ${fmt(p.values[key])}`).join('') + '.';
      });
    }

    diffText(p) {
      const top = this.api.AXES
        .map((key) => ({ key, gap: p.values[key] - this.current.values[key] }))
        .filter((g) => Math.abs(g.gap) >= 0.5)
        .sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap))
        .slice(0, 2);
      if (!top.length) return 'Very similar playing profile';
      return top.map((g) => `${g.gap > 0 ? 'More' : 'Less'} ${NOUN[g.key]}`).join(' · ');
    }

    priceNode(p) {
      return h('span', { class: 'rpp__card-price' },
        `${p.priceVaries ? 'From ' : ''}${this.money.format(p.price)}`,
        p.cap > p.price ? h('s', { text: this.money.format(p.cap) }) : null);
    }

    renderCards() {
      const handles = [...this.extra, ...this.suggested.map((p) => p.handle)].filter((handle, i, all) => all.indexOf(handle) === i);
      const full = this.selected.length >= MAX_COMPARE;
      const cards = handles.map((handle) => {
        const p = this.byHandle(handle);
        if (!p) return null;
        const index = this.selected.indexOf(handle);
        const on = index > -1;
        const toggle = h('button', {
          type: 'button', class: 'rpp__toggle', 'data-rpp-toggle': handle,
          'aria-pressed': String(on), disabled: !on && full, text: on ? 'Comparing' : 'Compare',
        });
        toggle.addEventListener('click', () => this.toggle(handle));
        return h('li', { class: `rpp__card${on ? ` rpp__series--${index + 1}` : ''}`, 'data-selected': on },
          h('a', { class: 'rpp__card-media', href: p.url, tabindex: '-1', 'aria-hidden': 'true' },
            p.img ? h('img', { src: p.img, alt: '', loading: 'lazy', width: '176', height: '176' }) : null),
          h('div', { class: 'rpp__card-info' },
            h('a', { class: 'rpp__card-name', href: p.url, text: p.title }),
            this.priceNode(p),
            h('span', { class: 'rpp__card-diff', text: this.diffText(p) })),
          toggle);
      });
      this.querySelector('[data-rpp-cards]').replaceChildren(...cards.filter(Boolean));
    }

    toggle(handle) {
      const p = this.byHandle(handle);
      if (!p) return;
      const index = this.selected.indexOf(handle);
      if (index > -1) this.selected.splice(index, 1);
      else if (this.selected.length < MAX_COMPARE) this.selected.push(handle);
      else return;
      this.render();
      this.querySelector(`[data-rpp-toggle="${CSS.escape(handle)}"]`)?.focus();
      this.announce(index > -1
        ? `Removed ${p.title} from the comparison.`
        : `Comparing with ${p.title}.${this.selected.length >= MAX_COMPARE ? ' Remove one to add another.' : ''}`);
    }

    renderFullLink() {
      const link = this.querySelector('[data-rpp-full]');
      if (!link || !this.dataset.comparatorUrl) return;
      const url = new URL(this.dataset.comparatorUrl, location.origin);
      url.searchParams.set('rackets', this.compared().map((p) => p.handle).join(','));
      link.href = url.pathname + url.search;
    }

    bindSearch() {
      const input = this.querySelector('[data-rpp-search]');
      const results = this.querySelector('[data-rpp-results]');
      const wrap = this.querySelector('.rpp__search');
      if (!input || !results || !wrap) return;
      const close = () => { results.hidden = true; input.setAttribute('aria-expanded', 'false'); };
      input.setAttribute('aria-expanded', 'false');
      input.addEventListener('input', () => {
        const query = input.value.trim();
        if (query.length < 2) return close();
        const exclude = [this.current.handle, ...this.selected];
        const found = this.api.search(this.products, query, '', false, exclude).slice(0, 6);
        const items = found.length
          ? found.map((p) => {
              const pick = h('button', { type: 'button', class: 'rpp__result' },
                h('span', { text: p.title }), h('span', { text: this.money.format(p.price) }));
              pick.addEventListener('click', () => { this.pick(p.handle, input, close); });
              return h('li', {}, pick);
            })
          : [h('li', { class: 'rpp__results-empty', text: 'No rated racket matches. Try a brand or model name.' })];
        results.replaceChildren(...items);
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      });
      input.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
      document.addEventListener('pointerdown', (event) => { if (!wrap.contains(event.target)) close(); });
    }

    async pick(handle, input, close) {
      close();
      const racket = await this.fetchOne(handle).catch(() => null);
      if (!racket) return void this.announce('That racket could not be loaded. Try another one.');
      if (!this.extra.includes(handle)) this.extra.unshift(handle);
      if (!this.selected.includes(handle)) {
        if (this.selected.length >= MAX_COMPARE) this.selected.shift();
        this.selected.push(handle);
      }
      input.value = '';
      close();
      this.render();
      this.announce(`Comparing with ${this.byHandle(handle)?.title}.`);
      this.querySelector(`[data-rpp-toggle="${CSS.escape(handle)}"]`)?.focus();
    }

    announce(message) {
      const status = this.querySelector('[data-rpp-status]');
      if (status) status.textContent = message;
    }
  }

  customElements.define('racket-play-profile', RacketPlayProfile);
})();
